import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowDown, FiArrowRight, FiArrowUpRight, FiGithub, FiLinkedin, FiMail } from 'react-icons/fi';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { EXPERIENCE, PROFILE, isCurrent } from '../../data/profile';
import { scrollToSection } from '../../scroll/scroll';
import ContourField from '../ContourField/ContourField';
import './Home.css';

gsap.registerPlugin(ScrollTrigger);

const latestJob = EXPERIENCE[0];

// Resized copies of the profile photo in portfolio.json, served with the site: the hero
// photo is the first thing to load, and the original is an 800px PNG of about 700 KB
const PORTRAIT = '/images/profile-640.webp';
const AVATAR = '/images/profile-160.webp';

const FACTS = [
  {
    to: 'experience',
    label: isCurrent(latestJob) ? 'Now' : 'Most recently',
    value: latestJob.role,
    detail: latestJob.stages ? `${latestJob.company}. Started as an intern, then kept on.` : latestJob.company,
  },
  {
    to: 'education',
    label: '3rd year result',
    value: 'First Class Honours',
    detail: `${PROFILE.education.degree.split(',')[0]}, ${PROFILE.education.school}. Graduating ${PROFILE.education.graduation}.`,
  },
  {
    to: 'projects',
    label: 'Side projects',
    value: 'Computer vision',
    detail: 'One system reads handwritten ballots. Another counts fish on 17 live ocean cameras.',
  },
];

export default function HomeSection() {
  const navigate = useNavigate();
  const rootRef = useRef(null);

  const go = (event, id) => {
    event.preventDefault();
    if (window.location.hash === `#${id}`) scrollToSection(id);
    else navigate(`/#${id}`);
  };

  // The portrait drifts a little slower than the page as the hero scrolls away.
  // The whole frame moves, never the photo inside it, so the photo is never enlarged.
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) return undefined;
    const context = gsap.context(() => {
      gsap.to('.portrait', {
        y: 60,
        ease: 'none',
        scrollTrigger: { trigger: rootRef.current, start: 'top top', end: 'bottom top', scrub: true },
      });
    }, rootRef);
    return () => context.revert();
  }, []);

  return (
    <section id="Home" ref={rootRef} className="hero" aria-labelledby="hero-title">
      <ContourField className="hero-contours" />
      <div className="container hero-layout">
        <div className="hero-copy">
          <div className="hero-kicker hero-in" style={{ '--d': '.05s' }}>
            <img className="hero-avatar-sm" src={AVATAR} alt="" width="56" height="56" decoding="async" />
            <span>Full stack developer · Mullingar, Ireland</span>
          </div>
          <h1 id="hero-title" className="hero-name">
            <span className="hero-line"><span style={{ '--d': '.1s' }}>Diarmuid</span></span>
            <span className="hero-line"><span style={{ '--d': '.2s' }}>Hession</span></span>
          </h1>
          <p className="hero-lead hero-in" style={{ '--d': '.38s' }}>
            I'm a final-year Software student at ATU Sligo. I like building the whole system: the model, the API and the interface people use.
          </p>
          <div className="hero-actions hero-in" style={{ '--d': '.48s' }}>
            <a href="#projects" className="btn-brand" onClick={(e) => go(e, 'projects')}>See my work <FiArrowDown className="icon-down" aria-hidden="true" /></a>
            <Link to="/cv" className="btn-brand-outline">View CV <FiArrowUpRight className="icon-out" aria-hidden="true" /></Link>
          </div>
          <ul className="hero-socials hero-in" style={{ '--d': '.58s' }} aria-label="Profiles">
            <li><a href={PROFILE.links.github} target="_blank" rel="noopener noreferrer"><FiGithub aria-hidden="true" /> GitHub</a></li>
            <li><a href={PROFILE.links.linkedin} target="_blank" rel="noopener noreferrer"><FiLinkedin aria-hidden="true" /> LinkedIn</a></li>
            <li><a href={`mailto:${PROFILE.email}`}><FiMail aria-hidden="true" /> {PROFILE.email}</a></li>
          </ul>
        </div>
        <div className="hero-aside">
          <div className="portrait">
            <span className="portrait-block" aria-hidden="true" />
            <div className="portrait-frame">
              <img className="portrait-img" src={PORTRAIT} alt="Portrait of Diarmuid Hession" width="640" height="640" fetchPriority="high" />
            </div>
          </div>
        </div>
      </div>
      <div className="container hero-facts-wrap">
        <ul className="hero-facts">
          {FACTS.map((fact, i) => (
            <li key={fact.to} className="hero-in" style={{ '--d': `${0.62 + i * 0.08}s` }}>
              <a href={`#${fact.to}`} className="hero-fact" onClick={(e) => go(e, fact.to)}>
                <span className="hero-fact-label">{fact.label}</span>
                <strong className="hero-fact-value">{fact.value}</strong>
                <span className="hero-fact-detail">{fact.detail}</span>
                <FiArrowRight className="hero-fact-arrow" aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
